// src/components/UserProfilePage.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../services/firebase';
import PasswordChangeModal from './PasswordChangeModal';

// *** UPDATE: Robuster StatsDisplay mit Number() Casting ***
const StatsDisplay = ({ title, stats, loading }) => {
  if (loading) {
    return <p>Lade {title}...</p>;
  }
  if (stats.length === 0) {
    return (
        <div className="mb-4">
            <h4 className="text-md font-bold mb-2">{title}</h4>
            <p className="text-gray-500 dark:text-gray-400">Noch keine Daten vorhanden.</p>
        </div>
    );
  }

  return (
    <div className="mb-4">
        <h4 className="text-md font-bold mb-2">{title}</h4>
        <div className="space-y-3 text-sm max-h-48 overflow-y-auto pr-2">
            {stats.map(stat => {
                // FIX: Sicherstellen, dass wir mit Zahlen rechnen
                const totalMin = Number(stat.totalMinutes) || 0;
                const count = Number(stat.meetingCount) || 0;
                const avgMinutes = count > 0 ? (totalMin / count).toFixed(0) : 0;

                return (
                    <div key={stat.month} className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <p className="font-semibold text-base">{new Date(stat.month + '-02').toLocaleString('de-DE', { month: 'long', year: 'numeric' })}</p>
                        <p className="text-gray-600 dark:text-gray-400">Gesamtzeit: <strong>{totalMin} Minuten</strong></p>
                        <p className="text-gray-600 dark:text-gray-400">Meetings: <strong>{count}</strong></p>
                        <p className="text-gray-600 dark:text-gray-400">Durchschnitt: <strong>~{avgMinutes} Min./Meeting</strong></p>
                    </div>
                );
            })}
        </div>
    </div>
  );
};

const SettingsRow = ({ label, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 border-b border-gray-200 dark:border-gray-700">
    <label className="text-gray-700 dark:text-gray-300 font-medium mb-2 sm:mb-0">{label}</label>
    <div className="w-full sm:w-1/2 md:w-56">{children}</div>
  </div>
);

const SettingsSelect = ({ value, onChange }) => (
  <select
    value={value}
    onChange={onChange}
    className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
  >
    <option value="bell">Nur Glocke (In-App)</option>
    <option value="email">Glocke & E-Mail</option>
    <option value="none">Keine</option>
  </select>
);


const UserProfilePage = ({ user, userProfile, onUpdateProfile, setNotification, companyId, onBack }) => {
  const [email, setEmail] = useState(user?.email || '');
  const [firstName, setFirstName] = useState(userProfile?.firstName || '');
  const [lastName, setLastName] = useState(userProfile?.lastName || '');
  const [photoURL, setPhotoURL] = useState(user?.photoURL || '');
  const [position, setPosition] = useState(userProfile?.position || '');
  const [department, setDepartment] = useState(userProfile?.department || '');
  const [isSaving, setIsSaving] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  const [internalStats, setInternalStats] = useState([]);
  const [externalStats, setExternalStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  const [settings, setSettings] = useState({
    defaultView: 'dashboard',
    preferredLanguage: 'de', 
    notifications: {
      onNewMeeting: 'bell',
      onNewTask: 'bell',
      onTaskCompleted: 'bell',
    }
  });

  useEffect(() => {
    setEmail(user?.email || '');
    setFirstName(userProfile?.firstName || '');
    setLastName(userProfile?.lastName || '');
    setPhotoURL(user?.photoURL || '');
    setPosition(userProfile?.position || '');
    setDepartment(userProfile?.department || '');
    
    setSettings({
      defaultView: userProfile?.settings?.defaultView || 'dashboard',
      preferredLanguage: userProfile?.preferredLanguage || 'de',
      notifications: {
        onNewMeeting: userProfile?.settings?.notifications?.onNewMeeting || 'bell',
        onNewTask: userProfile?.settings?.notifications?.onNewTask || 'bell',
        onTaskCompleted: userProfile?.settings?.notifications?.onTaskCompleted || 'bell',
      }
    });

  }, [user, userProfile]);


  useEffect(() => {
    if (user && companyId) {
      const fetchStats = async () => {
        setLoadingStats(true);
        try {
          // 1. Interne Stats laden
          const internalRef = collection(db, "companies", companyId, "users", user.uid, "attendanceStats");
          const qInternal = query(internalRef);
          const internalSnapshot = await getDocs(qInternal);
          const iStats = internalSnapshot.docs.map(doc => ({
            month: doc.id,
            ...doc.data()
          }));
          iStats.sort((a, b) => b.month.localeCompare(a.month));
          setInternalStats(iStats);

          // 2. Externe Stats laden
          const externalRef = collection(db, "companies", companyId, "users", user.uid, "externalAttendanceStats");
          const qExternal = query(externalRef);
          const externalSnapshot = await getDocs(qExternal);
          const eStats = externalSnapshot.docs.map(doc => ({
            month: doc.id,
            ...doc.data()
          }));
          eStats.sort((a, b) => b.month.localeCompare(a.month));
          setExternalStats(eStats);

        } catch (error) {
          console.error("Fehler beim Laden der Anwesenheitsstatistiken:", error);
          setInternalStats([]);
          setExternalStats([]);
          setNotification({ message: 'Fehler beim Laden der Anwesenheitsstatistiken. Bitte Berechtigungen prüfen.', type: 'error' });

        } finally {
          setLoadingStats(false);
        }
      };
      fetchStats();
    }
  }, [user, companyId, setNotification]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPhotoURL(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSettingsChange = (type, key, value) => {
    setSettings(prev => {
      if (type === 'notifications') {
        return {
          ...prev,
          notifications: { ...prev.notifications, [key]: value }
        };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdateProfile(user, {
        firstName, lastName, email, photoURL, position, department,
        settings: settings, 
        preferredLanguage: settings.preferredLanguage
      });
      setNotification({ message: 'Profil erfolgreich aktualisiert!', type: 'success' });
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Profils:", error);
      setNotification({ message: `Fehler: ${error.message}`, type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="p-4 space-y-8">
        <div className="flex items-center gap-4">
            <button onClick={onBack} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg">&larr; Zurück</button>
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white">Profil & Einstellungen</h2>
        </div>

        <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-grow">
                    <h3 className="text-xl font-bold mb-4">Persönliche Daten</h3>
                    <div className="flex flex-col items-center mb-6">
                      <div className="relative w-32 h-32 mb-4">
                        <img src={photoURL || 'https://placehold.co/128x128/e5e7eb/6b7280?text=User'} alt="Profilbild" className="w-full h-full rounded-full object-cover border-4 border-blue-200 dark:border-blue-700" />
                        <label htmlFor="photo-upload" className="absolute bottom-0 right-0 p-2 bg-blue-500 text-white rounded-full cursor-pointer hover:bg-blue-600 transition-colors shadow-lg">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.864-1.304A2 2 0 0110.457 2h3.085a2 2 0 011.664.89l.864 1.304A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                          <input id="photo-upload" type="file" className="hidden" onChange={handleFileChange} accept="image/*" />
                        </label>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="Vorname" />
                        <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="Nachname" />
                      </div>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="E-Mail" />
                      <input type="text" value={position} onChange={(e) => setPosition(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="Position" />
                      <input type="text" value={department} onChange={(e) => setDepartment(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="Abteilung" />
                    </div>
                </div>

                <div className="w-full md:w-72 flex-shrink-0 mt-8 md:mt-0">
                    <h3 className="text-lg font-bold mb-3 border-b pb-2">Anwesenheitsstatistik</h3>
                    
                    <StatsDisplay 
                        title="Interne Meetings"
                        stats={internalStats}
                        loading={loadingStats}
                    />
                    
                    <StatsDisplay 
                        title="Externe Meetings"
                        stats={externalStats}
                        loading={loadingStats}
                    />
                </div>
            </div>
          
            <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold mb-4">App-Einstellungen</h3>
              
              <SettingsRow label="Standard-Ansicht nach Login">
                <select
                  value={settings.defaultView}
                  onChange={(e) => handleSettingsChange('general', 'defaultView', e.target.value)}
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="dashboard">Dashboard</option>
                  <option value="list">Protokoll-Liste</option>
                  <option value="tasks">Meine Aufgaben</option>
                </select>
              </SettingsRow>
              
              <SettingsRow label="Bevorzugte Sprache">
                <select
                  value={settings.preferredLanguage}
                  onChange={(e) => handleSettingsChange('general', 'preferredLanguage', e.target.value)}
                  className="w-full p-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="de">Deutsch (DE)</option>
                  <option value="en">Englisch (EN)</option>
                </select>
              </SettingsRow>
              
              <h4 className="text-lg font-semibold mt-6 mb-2 text-gray-800 dark:text-white">Benachrichtigungen</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Wähle, wie du über wichtige Ereignisse informiert werden möchtest.</p>

              <SettingsRow label="...wenn ich zu einem Protokoll hinzugefügt werde">
                <SettingsSelect
                  value={settings.notifications.onNewMeeting}
                  onChange={(e) => handleSettingsChange('notifications', 'onNewMeeting', e.target.value)}
                />
              </SettingsRow>

              <SettingsRow label="...wenn mir eine neue Aufgabe zugewiesen wird">
                <SettingsSelect
                  value={settings.notifications.onNewTask}
                  onChange={(e) => handleSettingsChange('notifications', 'onNewTask', e.target.value)}
                />
              </SettingsRow>

              <SettingsRow label="...wenn eine von mir erstellte Aufgabe erledigt wird">
                <SettingsSelect
                  value={settings.notifications.onTaskCompleted}
                  onChange={(e) => handleSettingsChange('notifications', 'onTaskCompleted', e.target.value)}
                />
              </SettingsRow>
            </div>

            <div className="mt-10 border-t pt-6 border-gray-200 dark:border-gray-700">
                <button onClick={() => setShowPasswordChange(true)} className="w-full sm:w-auto text-left px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    Passwort ändern
                </button>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={handleSave} disabled={isSaving} className={`px-8 py-3 rounded-lg text-white font-bold text-base ${isSaving ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {isSaving ? 'Speichern...' : 'Alle Änderungen speichern'}
              </button>
            </div>
        </div>
      </div>
      <PasswordChangeModal isOpen={showPasswordChange} onClose={() => setShowPasswordChange(false)} setNotification={setNotification} />
    </>
  );
};

export default UserProfilePage;